import { create } from 'zustand';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AuthUser = Record<string, any>;

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: AuthUser) => void;
  setAccessToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  login: (user: AuthUser, accessToken: string, refreshToken?: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

function loadSession(): { user: AuthUser | null; accessToken: string | null; refreshToken: string | null } {
  try {
    const raw = sessionStorage.getItem('hf_auth');
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        user: parsed.user ?? null,
        accessToken: parsed.accessToken ?? null,
        refreshToken: parsed.refreshToken ?? null,
      };
    }
  } catch {}
  return { user: null, accessToken: null, refreshToken: null };
}

function saveSession(user: AuthUser | null, accessToken: string | null, refreshToken: string | null) {
  if (user && accessToken) {
    sessionStorage.setItem('hf_auth', JSON.stringify({ user, accessToken, refreshToken }));
  } else {
    sessionStorage.removeItem('hf_auth');
  }
}

const initial = loadSession();

export const useAuthStore = create<AuthState>((set) => ({
  user: initial.user,
  accessToken: initial.accessToken,
  refreshToken: initial.refreshToken,
  isAuthenticated: !!initial.accessToken,
  isLoading: !initial.accessToken,

  setUser: (user) => set({ user, isAuthenticated: true }),

  setAccessToken: (accessToken) => {
    set((s) => {
      saveSession(s.user, accessToken, s.refreshToken);
      return { accessToken };
    });
  },

  setRefreshToken: (refreshToken) => {
    set((s) => {
      saveSession(s.user, s.accessToken, refreshToken);
      return { refreshToken };
    });
  },

  login: (user, accessToken, refreshToken) => {
    saveSession(user, accessToken, refreshToken ?? null);
    set({ user, accessToken, refreshToken: refreshToken ?? null, isAuthenticated: true, isLoading: false });
  },

  logout: () => {
    saveSession(null, null, null);
    set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, isLoading: false });
  },

  setLoading: (isLoading) => set({ isLoading }),
}));
